// The bottom-right stack of the chart's cards (R1517; the stack rules of
// card 08 of mockup round 086, ui-map-interface.md §17.3.06).
//
// The cards of words stand in one column in the bottom-right corner of the
// map, from the bottom up: the cartouche plate (fixed) and the legend card.
// **The two are one strip** (R2181): the legend stands directly on the
// plate with no gap, the seam is one rule (`cc-join-up` on the legend,
// `cc-join-dn` on the plate), and a minimized legend is a row of the strip at
// the plate's width. A card alone keeps its own frame. The
// stack holds no transient card: the coastal profile is the body of the
// ridge peek (R1821) and the harbour plan a section of the hold card
// (R1824), so a hover or a selection never adds a card here.
//
// **The column stands on the corner grid** (round 086 card 14): the pad of
// `[presentation.layout]` inside the edge of the dock column. No card
// states an offset of its own. A mirrored desktop (R2883) stands it in the
// bottom-left corner, one pad inside the left edge.
//
// **The column never lies on the console** (R2057). A column whose span
// would cover the compass rose, the round minimap or an open chat box
// stands one `stack_gap` over the top of the highest of them
// (`floorOver` of `ui/layout.ts`), so the – of the minimap never lies on a
// row of the legend. The column stands again on a change of the console.
//
// **The desktop VIEW stands on the strip** (R2904). The stack hands the
// strip's top to `ui/layout.ts` (`setStripTop`) after each placement, and
// null while no card stands; VIEW stands one `BUTTON_GAP` over it at the
// strip's outer edge and moves with it. VIEW is no console box of a
// desktop, so the column never lifts over it and the two never loop.
//
// **The column never reaches the notice band.** When the column and VIEW
// on it would pass the foot of `#notices` (or the readout line while no
// notice stands), the legend folds to its head row.
//
// **The stack is the legend window, one unit** (R2832). The LEGEND tile of
// the VIEW panel (the setting `factionKey` of a desktop) turns the whole
// column off and on: the legend card with its FACTIONS, HEAT, DEPTH and
// CURRENT sections, and the cartouche plate. A column that is off stands
// no card on the glass, and each card keeps its own state for the next
// time the column is on. The overlay writes the switch on each draw of the
// map buttons (`setLegendWindow`).
//
// **A telephone keeps no card of the stack** (card 08): the plate and the
// legend draw on a desktop alone (`mobile = false` in `chart.toml`), the
// key is a section of the VIEW panel there and the heat legend the thin
// ramp at the edge (R1805, R1806).
//
// A card calls `stackCard` at the end of its render, when its height is the
// height of its words, and `unstackCard` in its unmount. Each call places
// every card on the glass again, so the last render of a tick leaves the
// stack right whatever the order of the renders. Nothing here runs per
// frame: a card renders on its own rebuild reasons (§10.3).

import { CHART } from '../../gen/presentation';
import { cssVarPx } from '../../fast/chart/dom';
import { BUTTON_GAP, PAD, consoleBoxes, floorOver, mirrorDesk, onCluster, setStripTop, viewPx } from '../layout';
import { markCornerGridDirty } from '../cornergrid';

/** The cards of the stack, bottom first. */
export type StackSlot = 'cartouche' | 'faction_key';
const ORDER: readonly StackSlot[] = ['cartouche', 'faction_key'];

/** One card: its element and the fold of the legend. */
interface Entry {
  el: HTMLElement;
  /** The legend's own fold to its head row, or nothing. */
  fold?: (on: boolean) => void;
}

const entries = new Map<StackSlot, Entry>();
/** The LEGEND tile: the legend window stands (R2832). */
let windowOn = true;
/** The stack follows each change of the console once a card stood. */
let clusterWired = false;

/** A card is on the glass, or it rendered again. `fold` is the legend's
 *  fold to its head row, which the stack calls when the column is too
 *  tall. */
export function stackCard(slot: StackSlot, el: HTMLElement, fold?: (on: boolean) => void): void {
  entries.set(slot, { el, fold });
  if (!clusterWired) {
    clusterWired = true;
    onCluster(restack);
  }
  restack();
}

/** The overlay's answer for the LEGEND tile of a desktop (R2832): the
 *  legend window stands, or no card of the stack does. A change places
 *  the column again at once. */
export function setLegendWindow(on: boolean): void {
  if (on === windowOn) return;
  windowOn = on;
  restack();
}

/** A card left the glass. */
export function unstackCard(slot: StackSlot): void {
  const e = entries.get(slot);
  if (!e) return;
  entries.delete(slot);
  e.el.style.display = '';
  e.el.classList.remove('cc-join-up', 'cc-join-dn');
  restack();
}

/**
 * The height the column may take, in px of the host: from the floor of the
 * column to the foot of the notice band, less one pad and less VIEW with
 * its gap on top of the strip (R2904). The band is the foot of `#notices`
 * while a notice stands, else the foot of the readout line. A host with no
 * layout answers Infinity, so nothing folds.
 */
function room(host: HTMLElement | null, floor: number): number {
  const hostH = host?.clientHeight ?? 0;
  if (hostH <= 0) return Infinity;
  let ceiling = cssVarPx('--topbar-h');
  const notices = typeof document === 'undefined' ? null : document.getElementById('notices');
  if (notices && notices.offsetHeight > 0) {
    ceiling = Math.max(ceiling, notices.offsetTop + notices.offsetHeight);
  }
  return hostH - floor - ceiling - PAD - viewPx() - BUTTON_GAP;
}

/** Stands one card `bottom` px over the floor of the host, at the corner
 *  of the column: one pad inside the dock, or one pad inside the left edge
 *  of a mirrored desktop (R2883). */
function standAt(el: HTMLElement, bottom: number): void {
  const s = el.style;
  s.position = 'absolute';
  s.top = s.transform = '';
  if (mirrorDesk()) {
    s.right = '';
    s.left = `${PAD}px`;
  } else {
    s.left = '';
    s.right = `${Math.round(PAD + cssVarPx('--dock-w'))}px`;
  }
  s.bottom = `${Math.round(bottom)}px`;
}

/** The span of the column, in px of a host `hostW` wide: one pad inside
 *  the dock, or one pad inside the left edge of a mirrored desktop (R2883).
 *  Pure. */
export function columnSpan(hostW: number, widest: number, dockW: number, mirror: boolean): [number, number] {
  if (mirror) return [PAD, PAD + widest];
  const right = hostW - PAD - dockW;
  return [right - widest, right];
}

/**
 * The floor of the column (R2057): the pad, lifted over every console box
 * under the span of the widest card. A host with no layout lifts nothing.
 */
function columnFloor(host: HTMLElement | null, cards: readonly HTMLElement[], gap: number): number {
  const hostW = host?.clientWidth ?? 0;
  if (hostW <= 0) return PAD;
  const widest = cards.reduce((w, el) => Math.max(w, el.offsetWidth), 0);
  const [left, right] = columnSpan(hostW, widest, cssVarPx('--dock-w'), mirrorDesk());
  return floorOver(left, right, consoleBoxes(), PAD, gap);
}

/** Places every card of the stack that is on the glass, bottom first. */
export function restack(): void {
  const gap = CHART.pieces.cartouche.fig.stack_gap;
  const live: [StackSlot, Entry][] = [];
  for (const slot of ORDER) {
    const e = entries.get(slot);
    if (e && e.el.isConnected) live.push([slot, e]);
  }
  // The graticule skips the stack (round 086 card 14), so it reads the
  // corner grid again.
  markCornerGridDirty();
  // No card stands: VIEW stands in the corner on the floor (R2904).
  if (live.length === 0) { setStripTop(null); return; }
  // The LEGEND tile is off (R2832): no card of the window stands.
  if (!windowOn) {
    for (const [, e] of live) {
      e.el.style.display = 'none';
      e.el.classList.remove('cc-join-up', 'cc-join-dn');
    }
    setStripTop(null);
    return;
  }
  const host = live[0][1].el.parentElement;
  // The column's floor: the pad over the screen's floor, or over the
  // console boxes under the column's span (R2057). The host lies over the
  // whole chrome, so its px are the console's chrome px.
  const floor = columnFloor(host, live.map(([, e]) => e.el), gap);
  const limit = room(host, floor);

  // Open everything first, then fold the legend when the room cannot
  // hold it.
  for (const [, e] of live) {
    e.el.style.display = '';
    e.fold?.(false);
  }
  // The legend on the plate is one strip (R2181): no gap between them,
  // and the classes join the two frames before anything is measured.
  const joined = live.length === 2;
  for (const [slot, e] of live) {
    e.el.classList.toggle('cc-join-up', joined && slot === 'faction_key');
    e.el.classList.toggle('cc-join-dn', joined && slot === 'cartouche');
  }
  const step = joined ? 0 : gap;
  const height = () => {
    const shown = live.filter(([, e]) => e.el.style.display !== 'none');
    return shown.reduce((h, [, e]) => h + e.el.offsetHeight, 0) + step * Math.max(0, shown.length - 1);
  };
  if (height() > limit) for (const [, e] of live) e.fold?.(true);

  let bottom = floor;
  for (const [, e] of live) {
    if (e.el.style.display === 'none') continue;
    standAt(e.el, bottom);
    bottom += e.el.offsetHeight + step;
  }
  // The desktop VIEW stands on the strip's top (R2904).
  setStripTop(floor + height());
}
